import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Form from '../form/Form';
import { modal } from '../modal/ModalEmitter';
import ApiService from '../../services/ApiService';
import browserHistory from '../../history';
import FeatureDisabled from './FeatureDisabled';
import { LIST_ITEM_DELIMITER } from '../../util/form';

const getLayout = (presets = {}, settings = {}, onVehicleChange, onDealChange, onAssigneeChange) => {
  const customer = {
    name: 'customerId',
    label: 'Customer',
    col: 8,
    validation: {
      page: 'EQS5',
      type: 'customer',
    },
    dependencies: {
      and: [{ '!=': ['dealId', ''] }],
      action: 'disable',
    },
  };

  const vehicle = {
    name: 'vehicleId',
    label: 'Vehicle',
    col: 8,
    validation: {
      page: 'AVI2',
      type: 'vehiclesale',
      url: '/crm/units',
      allowStates: settings.quoteAllowCreateUnitOnHold === 'Y' ? ['H'] : [],
    },
    events: {
      blur: onVehicleChange,
    },
    required: true,
  };

  const cost = {
    name: 'vehicleCost',
    label: 'Cost',
    col: 4,
    displayOnly: true,
    style: 'right line-item',
    type: 'currency',
    // dependencies: {
    //   and: [{ '==': ['vehicleCost', ''] }],
    //   action: 'hide',
    // },
  };

  const assignee = {
    name: 'assignedTo',
    label: 'Assigned To',
    // readOnlyOnInit: true,
    validation: {
      type: 'db',
      modelName: 'Salesperson',
      format: 'user',
    },
    control: 'dbselect',
    events: {
      blur: onAssigneeChange,
    },
  };

  const deal = {
    name: 'dealId',
    label: 'Add To Existing Deal',
    validation: {
      type: 'db',
      modelName: 'EQS2',
      emptyOption: { label: 'New Deal', value: '' },
    },
    control: 'dbselect',
    events: {
      blur: onDealChange,
    },
  };

  const branch = {
    name: 'branchId',
    label: 'Branch',
    control: 'dbselect',
    validation: {
      type: 'db',
      modelName: 'Branch',
    },
    dependencies: {
      and: [{ '==': ['dealId', ''] }],
      action: 'enable',
    },
  };

  if (presets.customer) {
    customer.data = presets.customer;
    customer.readOnlyOnInit = true;
  }

  if (presets.vehicle) {
    vehicle.data = presets.vehicle;
    vehicle.readOnlyOnInit = true;
  }

  if (presets.assignee) {
    assignee.data = presets.assignee;
    // assignee.readOnlyOnInit = true; // TODO dont set for manager
  }

  if (presets.branchId) {
    branch.data = presets.branchId;
  }

  if (presets.deal) {
    deal.data = presets.deal;
    deal.readOnly = true;
    deal.hidden = false;
  }

  return [
    deal,
    vehicle,
    cost,
    customer,
    {
      control: 'checkbox',
      label: 'Sales Tax Exempt',
      name: 'salesTaxExempt',
      style: 'text-right',
      data: 'Y',
      col: 4,
    },
    assignee,
    branch,
    { control: 'checkbox', label: 'Continue working on this quote', name: 'continue', data: 'Y' },
  ];
};

const QuickCreate = ({ presets = {}, submitCallback, lookupKey, contextName }) => {
  const { user, config, users } = useSelector(state => state.app);
  const [costsToDate, setCostsToDate] = useState('0');
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  const fetchDeal = dealId => {
    return ApiService.get(`crm/deal/${String(dealId)}`);
  };

  const fetchSettings = () => {
    return ApiService.get('/crm/settings').then(response => {
      setSettings(response.data);
      setLoading(false);
    });
  };

  const onVehicleChange = ({ fieldName, manager }) => {
    // TODO disable controls until check is done...
    const vehicleId = manager.getValue(fieldName);

    if (!vehicleId) {
      return;
    }

    ApiService.get(`crm/vehicle/${encodeURIComponent(vehicleId)}/meta`).then(response => {
      const vehicleCost = _.get(response, 'data.cost', 0);

      setCostsToDate(vehicleCost);

      // TODO ideally a loading mask or disable submit button, although not very important either
      if (manager && manager.formHandler && manager.formHandler.current) {
        manager.formHandler.current.updateForm({ vehicleCost });
      }
    });
  };

  const onDealChange = ({ fieldName, manager }) => {
    const dealId = manager.getValue(fieldName);

    if (!dealId) {
      return;
    }

    fetchDeal(dealId).then(response => {
      const branchId = _.get(response, 'data.branchId');
      if (branchId) {
        if (manager && manager.formHandler && manager.formHandler.current) {
          manager.formHandler.current.updateForm({
            branchId,
          });
        }
      }

      const customer = _.get(response, 'data.Customer');
      if (customer) {
        if (manager && manager.formHandler && manager.formHandler.current) {
          manager.formHandler.current.updateForm({
            customerId: `${customer.itemid}:${LIST_ITEM_DELIMITER}${customer.fullName}`,
          });
        }
      }
    });
  };

  const onAssigneeChange = ({ fieldName, manager }) => {
    const dealId = manager.getValue('dealId');

    // Only for new deals
    if (dealId !== '') {
      return;
    }

    const assignedTo = Number(manager.getValue(fieldName));
    const user = users.find(u => u.id === assignedTo);

    if (user) {
      if (manager && manager.formHandler && manager.formHandler.current) {
        manager.formHandler.current.updateForm({
          branchId: user.defaultBranchId,
        });
      }
    }
  };

  const handleSubmit = payload => {
    // TODO Check for used and start quick with FET exemption, make default for now
    const exemption = {
      exemptFET: 'Y',
      FETPercent: '0.00',
    };

    return ApiService.post('/crm/quote', { ...payload, data: { costsToDate, ...exemption } }).then(response => {
      // return ApiService.post('/db/EQS1', { ...payload, data: { costsToDate } }).then(response => {
      if (payload.continue === 'Y') {
        browserHistory.push(`/db/EQS2/${String(response.data.dealId)}/edit?oq=${String(response.data.id)}`);
      }
      modal.close();
      if (submitCallback) submitCallback(response, payload);
    });
  };

  useEffect(() => {
    if (!settings) {
      fetchSettings();
    }
  }, []);

  // Loading from a list on a stock number
  if (lookupKey && contextName === 'itemid') {
    presets.vehicle = lookupKey;
  }

  // If assignee preset isn't set, use current user
  if (!presets.assignee) {
    presets.assignee = user.id;
  }

  if (!presets.branchId) {
    presets.branchId = user.branch.id;
  }

  if (!config.features.equipment) {
    return <FeatureDisabled />;
  }

  if (loading) {
    return <div />;
  }

  return (
    <div className="quick-create">
      <Form
        layout={getLayout(presets, settings, onVehicleChange, onDealChange, onAssigneeChange)}
        trackDirty={false}
        onCancel={() => modal.close()}
        submit={handleSubmit}
        submitLabel="Begin"
        exportFormat="object"
        useDirtyWarning={false}
        submitAll
        showHelp={false}
      />
    </div>
  );
};

QuickCreate.propTypes = {
  presets: PropTypes.object,
};

export default QuickCreate;
